import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Repeat, Plus, Trash2, Play } from "lucide-react";

const INTERVALS = { weekly: "Semanal", monthly: "Mensal", annual: "Anual" };

export default function RecurringPage() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [recs, setRecs] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ customer_name: "", interval: "monthly", start_date: "" });
  const [items, setItems] = useState([{ name: "", quantity: "1", unit_price: "" }]);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API_URL}/recurring`, { headers, withCredentials: true });
      setRecs(res.data);
    } catch { toast.error("Erro ao carregar recorrências"); }
  };

  const create = async (e) => {
    e.preventDefault();
    const validItems = items.filter((i) => i.name && i.unit_price).map((i) => ({
      name: i.name, quantity: parseFloat(i.quantity) || 1, unit_price: toBaseCurrency(i.unit_price),
    }));
    if (validItems.length === 0) { toast.error("Adicione pelo menos um item"); return; }
    try {
      await axios.post(`${API_URL}/recurring`, {
        ...form, items: validItems, start_date: form.start_date || null,
      }, { headers, withCredentials: true });
      toast.success("Recorrência criada — as faturas serão geradas automaticamente");
      setModal(false); setForm({ customer_name: "", interval: "monthly", start_date: "" }); setItems([{ name: "", quantity: "1", unit_price: "" }]);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const runNow = async (r) => {
    try {
      const res = await axios.post(`${API_URL}/recurring/${r.recurring_id}/run-now`, {}, { headers, withCredentials: true });
      toast.success(res.data.message);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const toggle = async (r) => {
    await axios.put(`${API_URL}/recurring/${r.recurring_id}/toggle`, {}, { headers, withCredentials: true });
    fetchAll();
  };

  const remove = async (r) => {
    if (!window.confirm(`Remover recorrência de "${r.customer_name}"?`)) return;
    await axios.delete(`${API_URL}/recurring/${r.recurring_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  return (
    <div className="space-y-6" data-testid="recurring-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Faturação Recorrente</h1>
          <p className="text-muted-foreground">Faturas geradas automaticamente (avenças, mensalidades, subscrições)</p>
        </div>
        <Button onClick={() => setModal(true)} data-testid="add-recurring-btn"><Plus className="w-4 h-4 mr-2" /> Nova Recorrência</Button>
      </div>

      {recs.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><Repeat className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhuma recorrência configurada</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {recs.map((r) => (
            <Card key={r.recurring_id} data-testid={`recurring-${r.recurring_id}`}>
              <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold">{r.customer_name} <Badge variant="outline" className="ml-1">{INTERVALS[r.interval] || r.interval}</Badge></p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(r.estimated_total)} · próxima: {r.next_run} · {r.generated_count || 0} fatura(s) gerada(s)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={r.active ? "bg-emerald-500/20 text-emerald-600" : "bg-slate-500/20 text-slate-500"}>{r.active ? "Ativa" : "Pausada"}</Badge>
                  <Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`toggle-recurring-${r.recurring_id}`} />
                  <Button size="sm" variant="outline" onClick={() => runNow(r)} data-testid={`run-recurring-${r.recurring_id}`}><Play className="w-4 h-4 mr-1" /> Gerar Agora</Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(r)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nova Faturação Recorrente</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="space-y-1"><Label>Cliente *</Label><Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="recurring-customer-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Intervalo</Label>
                <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v })}>
                  <SelectTrigger data-testid="recurring-interval-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="monthly">Mensal</SelectItem>
                    <SelectItem value="annual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Primeira Emissão</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            </div>
            <Label>Itens (IVA 14% incluído no cálculo)</Label>
            <div className="space-y-2 max-h-44 overflow-y-auto">
              {items.map((it, i) => (
                <div key={i} className="flex gap-2">
                  <Input className="flex-1" placeholder="Descrição *" value={it.name} onChange={(e) => { const a = [...items]; a[i].name = e.target.value; setItems(a); }} />
                  <Input className="w-16" type="number" step="0.01" min="0.01" placeholder="Qtd" value={it.quantity} onChange={(e) => { const a = [...items]; a[i].quantity = e.target.value; setItems(a); }} />
                  <Input className="w-28" type="number" step="0.01" min="0.01" placeholder="Preço *" value={it.unit_price} onChange={(e) => { const a = [...items]; a[i].unit_price = e.target.value; setItems(a); }} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" onClick={() => setItems([...items, { name: "", quantity: "1", unit_price: "" }])}><Plus className="w-4 h-4 mr-1" /> Item</Button>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-recurring-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
